/**
 * Permission Management API Types
 *
 * Types for managing role assignment permissions and permission bundles.
 */

import {
    AuthorizableAction,
    ResourceType,
    ALL_AUTHORIZABLE_ACTIONS,
    isAllowedActionForResourceType,
} from './authorization-contracts';
import { Role, Status } from '@pillar-portal/database/dist/enums';
import { z } from 'zod';
import { CombinedConfigResponse } from './samba-config.types';
import { CombinedConfigResponseSchema } from './schemas/samba-config.schemas';
import type { OpaqueCursorPagination } from './authorization.types';
import type { OpaqueCursorSearchRequest } from './omnisearch.types';
import {
    RpaModuleName,
    RpaModuleNameSchema,
} from './backend_config.types';
import {
    AllInternalOnly,
    AllOrganizationalOnly,
} from '@pillar-portal/database/dist/enums';
import {
    IpgSystemId,
    IpwSystemId,
} from './types/third-party-system.enums';

export interface OrganizationResourceDesignator {
    designator_type: typeof PermissionSourceDesignatorType.ORGANIZATION;
    organization_uuid: string;
    descendants: boolean;
}

export interface UserResourceDesignator {
    designator_type: typeof PermissionSourceDesignatorType.USER;
    user_uuid: string;
}

export interface RoleAssignmentResourceDesignator {
    designator_type: typeof PermissionSourceDesignatorType.ROLE_ASSIGNMENT;
    role_assignment_uuid: string;
}

export interface CredentialResourceDesignator {
    designator_type: typeof PermissionSourceDesignatorType.CREDENTIAL;
    credential_uuid: string;
}

export interface OrganizationIpxCredentialPolicyResourceDesignator {
    designator_type:
        typeof PermissionSourceDesignatorType.ORGANIZATION_IPX_CREDENTIAL_POLICY;
    organization_ipx_credential_policy_uuid: string;
}

export interface IpxCredentialDefinitionResourceDesignator {
    designator_type:
        typeof PermissionSourceDesignatorType.IPX_CREDENTIAL_DEFINITION;
    ipx_credential_definition_uuid: string;
}

export interface IpxWebResourceModelResourceDesignator {
    designator_type:
        typeof PermissionSourceDesignatorType.IPX_WEB_RESOURCE_MODEL;
    ipx_web_resource_model_uuid: string;
}

export interface AllowedWebsiteTemplateResourceDesignator {
    designator_type:
        typeof PermissionSourceDesignatorType.ALLOWED_WEBSITE_TEMPLATE;
    allowed_website_template_uuid: string;
}

export interface IpwResourceDesignator {
    designator_type: typeof PermissionSourceDesignatorType.IPW;
    ipw_system_id: IpwSystemId;
}

export interface IpgResourceDesignator {
    designator_type: typeof PermissionSourceDesignatorType.IPG;
    ipg_system_id: IpgSystemId;
}

export interface ProcessorResourceDesignator {
    designator_type: typeof PermissionSourceDesignatorType.PROCESSOR;
    processor_uuid: string;
}

export interface MerchantAccountResourceDesignator {
    designator_type: typeof PermissionSourceDesignatorType.MERCHANT_ACCOUNT;
    merchant_account_uuid: string;
}

export interface LogicalTerminalResourceDesignator {
    designator_type: typeof PermissionSourceDesignatorType.LOGICAL_TERMINAL;
    logical_terminal_uuid: string;
}

export interface TerminalDeviceResourceDesignator {
    designator_type: typeof PermissionSourceDesignatorType.TERMINAL_DEVICE;
    terminal_device_uuid: string;
}

export interface PaymentTransactionResourceDesignator {
    designator_type: typeof PermissionSourceDesignatorType.PAYMENT_TRANSACTION;
    payment_transaction_uuid: string;
}

export interface PaymentRequestResourceDesignator {
    designator_type: typeof PermissionSourceDesignatorType.PAYMENT_REQUEST;
    payment_request_uuid: string;
}

export interface PaymentMethodTokenResourceDesignator {
    designator_type: typeof PermissionSourceDesignatorType.PAYMENT_METHOD_TOKEN;
    payment_method_token_uuid: string;
}

export interface PermissionsBundleResourceDesignator {
    designator_type: typeof PermissionSourceDesignatorType.PERMISSIONS_BUNDLE;
    permissions_bundle_uuid: string;
}

export interface PermissionPolicyResourceDesignator {
    designator_type: typeof PermissionSourceDesignatorType.PERMISSION_POLICY;
    permission_policy_uuid: string;
}

export interface RpaModuleResourceDesignator {
    designator_type: typeof PermissionSourceDesignatorType.RPA_MODULE;
    rpa_module_name: RpaModuleName;
}

export const PermissionSourceDesignatorType = {
    ORGANIZATION: ResourceType.ORGANIZATION,
    USER: ResourceType.USER,
    ROLE_ASSIGNMENT: ResourceType.ROLE_ASSIGNMENT,
    CREDENTIAL: ResourceType.CREDENTIAL,
    ORGANIZATION_IPX_CREDENTIAL_POLICY:
        ResourceType.ORGANIZATION_IPX_CREDENTIAL_POLICY,
    IPX_CREDENTIAL_DEFINITION: ResourceType.IPX_CREDENTIAL_DEFINITION,
    IPX_WEB_RESOURCE_MODEL: ResourceType.IPX_WEB_RESOURCE_MODEL,
    ALLOWED_WEBSITE_TEMPLATE: ResourceType.ALLOWED_WEBSITE_TEMPLATE,
    IPW: ResourceType.IPW,
    IPG: ResourceType.IPG,
    PROCESSOR: ResourceType.PROCESSOR,
    MERCHANT_ACCOUNT: ResourceType.MERCHANT_ACCOUNT,
    LOGICAL_TERMINAL: ResourceType.LOGICAL_TERMINAL,
    TERMINAL_DEVICE: ResourceType.TERMINAL_DEVICE,
    PAYMENT_TRANSACTION: ResourceType.PAYMENT_TRANSACTION,
    PAYMENT_REQUEST: ResourceType.PAYMENT_REQUEST,
    PAYMENT_METHOD_TOKEN: ResourceType.PAYMENT_METHOD_TOKEN,
    PERMISSIONS_BUNDLE: ResourceType.PERMISSIONS_BUNDLE,
    PERMISSION_POLICY: ResourceType.PERMISSION_POLICY,
    RPA_MODULE: ResourceType.RPA_MODULE,
    WILDCARD: 'WILDCARD',
    SELF: 'SELF',
} as const;

export type PermissionSourceDesignatorType =
    (typeof PermissionSourceDesignatorType)[keyof typeof PermissionSourceDesignatorType];

export interface SelfSourceDesignator {
    designator_type: typeof PermissionSourceDesignatorType.SELF;
}
export interface WildcardPermissionSourceDesignator {
    designator_type: typeof PermissionSourceDesignatorType.WILDCARD;
}

export type ResourceDesignator =
    | OrganizationResourceDesignator
    | UserResourceDesignator
    | RoleAssignmentResourceDesignator
    | CredentialResourceDesignator
    | OrganizationIpxCredentialPolicyResourceDesignator
    | IpxCredentialDefinitionResourceDesignator
    | IpxWebResourceModelResourceDesignator
    | AllowedWebsiteTemplateResourceDesignator
    | IpwResourceDesignator
    | IpgResourceDesignator
    | ProcessorResourceDesignator
    | MerchantAccountResourceDesignator
    | LogicalTerminalResourceDesignator
    | TerminalDeviceResourceDesignator
    | PaymentTransactionResourceDesignator
    | PaymentRequestResourceDesignator
    | PaymentMethodTokenResourceDesignator
    | PermissionsBundleResourceDesignator
    | PermissionPolicyResourceDesignator
    | RpaModuleResourceDesignator;

export type PermissionSourceDesignator =
    | ResourceDesignator
    | SelfSourceDesignator
    | WildcardPermissionSourceDesignator;

const OrganizationResourceDesignatorSchemaBase = z.object({
    designator_type: z.literal(PermissionSourceDesignatorType.ORGANIZATION),
    organization_uuid: z.string().uuid(),
    descendants: z.boolean().default(false),
});
export const OrganizationResourceDesignatorSchema: typeof OrganizationResourceDesignatorSchemaBase =
    OrganizationResourceDesignatorSchemaBase;

const UserResourceDesignatorSchemaBase = z.object({
    designator_type: z.literal(PermissionSourceDesignatorType.USER),
    user_uuid: z.string().uuid(),
});
export const UserResourceDesignatorSchema: typeof UserResourceDesignatorSchemaBase =
    UserResourceDesignatorSchemaBase;

const RoleAssignmentResourceDesignatorSchemaBase = z.object({
    designator_type: z.literal(PermissionSourceDesignatorType.ROLE_ASSIGNMENT),
    role_assignment_uuid: z.string().uuid(),
});
export const RoleAssignmentResourceDesignatorSchema: typeof RoleAssignmentResourceDesignatorSchemaBase =
    RoleAssignmentResourceDesignatorSchemaBase;

const CredentialResourceDesignatorSchemaBase = z.object({
    designator_type: z.literal(PermissionSourceDesignatorType.CREDENTIAL),
    credential_uuid: z.string().uuid(),
});
export const CredentialResourceDesignatorSchema: typeof CredentialResourceDesignatorSchemaBase =
    CredentialResourceDesignatorSchemaBase;

const OrganizationIpxCredentialPolicyResourceDesignatorSchemaBase = z.object({
    designator_type: z.literal(
        PermissionSourceDesignatorType.ORGANIZATION_IPX_CREDENTIAL_POLICY
    ),
    organization_ipx_credential_policy_uuid: z.string().uuid(),
});
export const OrganizationIpxCredentialPolicyResourceDesignatorSchema: typeof OrganizationIpxCredentialPolicyResourceDesignatorSchemaBase =
    OrganizationIpxCredentialPolicyResourceDesignatorSchemaBase;

const IpxCredentialDefinitionResourceDesignatorSchemaBase = z.object({
    designator_type: z.literal(
        PermissionSourceDesignatorType.IPX_CREDENTIAL_DEFINITION
    ),
    ipx_credential_definition_uuid: z.string().uuid(),
});
export const IpxCredentialDefinitionResourceDesignatorSchema: typeof IpxCredentialDefinitionResourceDesignatorSchemaBase =
    IpxCredentialDefinitionResourceDesignatorSchemaBase;

const IpxWebResourceModelResourceDesignatorSchemaBase = z.object({
    designator_type: z.literal(
        PermissionSourceDesignatorType.IPX_WEB_RESOURCE_MODEL
    ),
    ipx_web_resource_model_uuid: z.string().uuid(),
});
export const IpxWebResourceModelResourceDesignatorSchema: typeof IpxWebResourceModelResourceDesignatorSchemaBase =
    IpxWebResourceModelResourceDesignatorSchemaBase;

const AllowedWebsiteTemplateResourceDesignatorSchemaBase = z.object({
    designator_type: z.literal(
        PermissionSourceDesignatorType.ALLOWED_WEBSITE_TEMPLATE
    ),
    allowed_website_template_uuid: z.string().uuid(),
});
export const AllowedWebsiteTemplateResourceDesignatorSchema: typeof AllowedWebsiteTemplateResourceDesignatorSchemaBase =
    AllowedWebsiteTemplateResourceDesignatorSchemaBase;

const IpwResourceDesignatorSchemaBase = z.object({
    designator_type: z.literal(PermissionSourceDesignatorType.IPW),
    ipw_system_id: z.union([
        z.literal(IpwSystemId.DEALERTRACK),
        z.literal(IpwSystemId.PCPARTPICKER),
    ]),
});
export const IpwResourceDesignatorSchema: typeof IpwResourceDesignatorSchemaBase =
    IpwResourceDesignatorSchemaBase;

const IpgResourceDesignatorSchemaBase = z.object({
    designator_type: z.literal(PermissionSourceDesignatorType.IPG),
    ipg_system_id: z.literal(IpgSystemId.VALOR_US),
});
export const IpgResourceDesignatorSchema: typeof IpgResourceDesignatorSchemaBase =
    IpgResourceDesignatorSchemaBase;

const ProcessorResourceDesignatorSchemaBase = z.object({
    designator_type: z.literal(PermissionSourceDesignatorType.PROCESSOR),
    processor_uuid: z.string().uuid(),
});
export const ProcessorResourceDesignatorSchema: typeof ProcessorResourceDesignatorSchemaBase =
    ProcessorResourceDesignatorSchemaBase;

const MerchantAccountResourceDesignatorSchemaBase = z.object({
    designator_type: z.literal(PermissionSourceDesignatorType.MERCHANT_ACCOUNT),
    merchant_account_uuid: z.string().uuid(),
});
export const MerchantAccountResourceDesignatorSchema: typeof MerchantAccountResourceDesignatorSchemaBase =
    MerchantAccountResourceDesignatorSchemaBase;

const LogicalTerminalResourceDesignatorSchemaBase = z.object({
    designator_type: z.literal(PermissionSourceDesignatorType.LOGICAL_TERMINAL),
    logical_terminal_uuid: z.string().uuid(),
});
export const LogicalTerminalResourceDesignatorSchema: typeof LogicalTerminalResourceDesignatorSchemaBase =
    LogicalTerminalResourceDesignatorSchemaBase;

const TerminalDeviceResourceDesignatorSchemaBase = z.object({
    designator_type: z.literal(PermissionSourceDesignatorType.TERMINAL_DEVICE),
    terminal_device_uuid: z.string().uuid(),
});
export const TerminalDeviceResourceDesignatorSchema: typeof TerminalDeviceResourceDesignatorSchemaBase =
    TerminalDeviceResourceDesignatorSchemaBase;

const PaymentTransactionResourceDesignatorSchemaBase = z.object({
    designator_type: z.literal(PermissionSourceDesignatorType.PAYMENT_TRANSACTION),
    payment_transaction_uuid: z.string().uuid(),
});
export const PaymentTransactionResourceDesignatorSchema: typeof PaymentTransactionResourceDesignatorSchemaBase =
    PaymentTransactionResourceDesignatorSchemaBase;

const PaymentRequestResourceDesignatorSchemaBase = z.object({
    designator_type: z.literal(PermissionSourceDesignatorType.PAYMENT_REQUEST),
    payment_request_uuid: z.string().uuid(),
});
export const PaymentRequestResourceDesignatorSchema: typeof PaymentRequestResourceDesignatorSchemaBase =
    PaymentRequestResourceDesignatorSchemaBase;

const PaymentMethodTokenResourceDesignatorSchemaBase = z.object({
    designator_type: z.literal(PermissionSourceDesignatorType.PAYMENT_METHOD_TOKEN),
    payment_method_token_uuid: z.string().uuid(),
});
export const PaymentMethodTokenResourceDesignatorSchema: typeof PaymentMethodTokenResourceDesignatorSchemaBase =
    PaymentMethodTokenResourceDesignatorSchemaBase;

const PermissionsBundleResourceDesignatorSchemaBase = z.object({
    designator_type: z.literal(PermissionSourceDesignatorType.PERMISSIONS_BUNDLE),
    permissions_bundle_uuid: z.string().uuid(),
});
export const PermissionsBundleResourceDesignatorSchema: typeof PermissionsBundleResourceDesignatorSchemaBase =
    PermissionsBundleResourceDesignatorSchemaBase;

const PermissionPolicyResourceDesignatorSchemaBase = z.object({
    designator_type: z.literal(PermissionSourceDesignatorType.PERMISSION_POLICY),
    permission_policy_uuid: z.string().uuid(),
});
export const PermissionPolicyResourceDesignatorSchema: typeof PermissionPolicyResourceDesignatorSchemaBase =
    PermissionPolicyResourceDesignatorSchemaBase;

const RpaModuleResourceDesignatorSchemaBase = z.object({
    designator_type: z.literal(PermissionSourceDesignatorType.RPA_MODULE),
    rpa_module_name: RpaModuleNameSchema,
});
export const RpaModuleResourceDesignatorSchema: typeof RpaModuleResourceDesignatorSchemaBase =
    RpaModuleResourceDesignatorSchemaBase;

const ResourceDesignatorSchemaBase = z.discriminatedUnion('designator_type', [
    OrganizationResourceDesignatorSchemaBase,
    UserResourceDesignatorSchemaBase,
    RoleAssignmentResourceDesignatorSchemaBase,
    CredentialResourceDesignatorSchemaBase,
    OrganizationIpxCredentialPolicyResourceDesignatorSchemaBase,
    IpxCredentialDefinitionResourceDesignatorSchemaBase,
    IpxWebResourceModelResourceDesignatorSchemaBase,
    AllowedWebsiteTemplateResourceDesignatorSchemaBase,
    IpwResourceDesignatorSchemaBase,
    IpgResourceDesignatorSchemaBase,
    ProcessorResourceDesignatorSchemaBase,
    MerchantAccountResourceDesignatorSchemaBase,
    LogicalTerminalResourceDesignatorSchemaBase,
    TerminalDeviceResourceDesignatorSchemaBase,
    PaymentTransactionResourceDesignatorSchemaBase,
    PaymentRequestResourceDesignatorSchemaBase,
    PaymentMethodTokenResourceDesignatorSchemaBase,
    PermissionsBundleResourceDesignatorSchemaBase,
    PermissionPolicyResourceDesignatorSchemaBase,
    RpaModuleResourceDesignatorSchemaBase,
]);
export const ResourceDesignatorSchema: typeof ResourceDesignatorSchemaBase =
    ResourceDesignatorSchemaBase;

const SelfSourceDesignatorSchemaBase = z.object({
    designator_type: z.literal(PermissionSourceDesignatorType.SELF),
});
export const SelfSourceDesignatorSchema: typeof SelfSourceDesignatorSchemaBase =
    SelfSourceDesignatorSchemaBase;

const WildcardPermissionSourceDesignatorSchemaBase = z.object({
    designator_type: z.literal(PermissionSourceDesignatorType.WILDCARD),
});
export const WildcardPermissionSourceDesignatorSchema: typeof WildcardPermissionSourceDesignatorSchemaBase =
    WildcardPermissionSourceDesignatorSchemaBase;

const PermissionSourceDesignatorSchemaBase = z.discriminatedUnion('designator_type', [
    OrganizationResourceDesignatorSchemaBase,
    UserResourceDesignatorSchemaBase,
    RoleAssignmentResourceDesignatorSchemaBase,
    CredentialResourceDesignatorSchemaBase,
    OrganizationIpxCredentialPolicyResourceDesignatorSchemaBase,
    IpxCredentialDefinitionResourceDesignatorSchemaBase,
    IpxWebResourceModelResourceDesignatorSchemaBase,
    AllowedWebsiteTemplateResourceDesignatorSchemaBase,
    IpwResourceDesignatorSchemaBase,
    IpgResourceDesignatorSchemaBase,
    ProcessorResourceDesignatorSchemaBase,
    MerchantAccountResourceDesignatorSchemaBase,
    LogicalTerminalResourceDesignatorSchemaBase,
    TerminalDeviceResourceDesignatorSchemaBase,
    PaymentTransactionResourceDesignatorSchemaBase,
    PaymentRequestResourceDesignatorSchemaBase,
    PaymentMethodTokenResourceDesignatorSchemaBase,
    PermissionsBundleResourceDesignatorSchemaBase,
    PermissionPolicyResourceDesignatorSchemaBase,
    RpaModuleResourceDesignatorSchemaBase,
    SelfSourceDesignatorSchemaBase,
    WildcardPermissionSourceDesignatorSchemaBase,
]);
export const PermissionSourceDesignatorSchema: z.ZodType<PermissionSourceDesignator> =
    PermissionSourceDesignatorSchemaBase;

/**
 * allow or deny the action on the specified resources
 */
export type PermissionEffect = 'allow' | 'deny';

const PermissionEffectSchemaBase = z.enum(['allow', 'deny']);
export const PermissionEffectSchema: z.ZodType<PermissionEffect> =
    PermissionEffectSchemaBase;

/**
 * Permission grant/permission object
 */
export interface PermissionsGrant {
    effect: PermissionEffect; // default to 'allow'
    resource_type: ResourceType;
    resources: PermissionSourceDesignator[];
    actions: AuthorizableAction[];
}

/**
 * Permission bundle
 */
export interface PermissionsBundle {
    permissions_bundle_id: number;
    name: string;
    description: string | null;
    permissions: PermissionsGrant[];
    is_personal: boolean;
    status: Status;
}

export interface PermissionResourceDisplayMetadata {
    designator_key: string;
    label: string;
}

export interface PermissionsBundlePublic {
    permissions_bundle_uuid: string;
    name: string;
    description: string | null;
    permissions: PermissionsGrant[];
    is_personal: boolean;
    status: Status;
    resource_display_metadata?: PermissionResourceDisplayMetadata[];
    permissions_deserialization_error?: string;
}

export interface PolicyPermissionBundleListItem extends PermissionsBundlePublic {
    granted_role_assignment_count: number;
}

export interface PermissionSourceRoleAssignmentListItem {
    role_assignment_uuid: string;
    user_uuid: string;
    user_display_name: string;
    primary_email: string;
    role: Role;
    organization_uuid: string;
    organization_name: string;
}

export interface OrganizationScopedPermissionSourceOptionsResponse {
    enabled_ipw_system_ids: IpwSystemId[];
    enabled_ipg_system_ids: IpgSystemId[];
}

/**
 * Role assignment for authorization checks
 * This is the canonical type used throughout the backend
 * Represents the active role assignment with its permission bundle
 */
export interface RoleAssignment {
    role_assignment_id: number;
    role_assignment_uuid?: string;
    user_id: number;
    organization_id: number | null;
    organization_uuid?: string;
    organization_status?: Status | null;
    status: Status;
    role: Role;
    organization_name: string | null; // Optional: organization name for display (frontend use)
    organization_is_iso: boolean;
    organization_is_merchant: boolean;
    organization_is_customer: boolean;
}

export interface RoleAssignmentPermissionContext {
    status: Status;
    role: Role;
    organization_name: string | null;
    organization_is_iso: boolean;
    organization_is_merchant: boolean;
    organization_is_customer: boolean;
}

export interface RoleAssignmentDetails {
    permissions_bundle: PermissionsBundle;
    combined_config: CombinedConfigResponse;
}

/**
 * Role assignment with permissions
 */
export type RoleAssignmentDetailed = RoleAssignment & RoleAssignmentDetails;

export interface RoleAssignmentGrantedPermissionPolicySummary {
    permissions_bundle_uuid: string;
    name: string;
    status: Status;
}

export interface RoleAssignmentPermissionsView extends RoleAssignmentPermissionContext {
    role_assignment_uuid: string;
    organization_uuid: string | null;
    permissions_bundle: PermissionsBundlePublic;
    granted_permission_policies: RoleAssignmentGrantedPermissionPolicySummary[];
    combined_config: CombinedConfigResponse;
    can_impersonate?: boolean;
}

/**
 * Grant permission request
 */
export interface GrantPermissionRequest {
    role_assignment_uuid: string;
    permission: PermissionsGrant;
}

/**
 * Revoke permission request
 */
export interface RevokePermissionRequest {
    role_assignment_uuid: string;
    permission: PermissionsGrant;
}

/**
 * Assign bundle request
 */
export interface AssignBundleRequest {
    role_assignment_uuid: string;
    bundle_uuid: string;
}

/**
 * Create shared bundle request
 */
export interface CreateSharedBundleRequest {
    name: string;
    description?: string;
    permissions: PermissionsGrant[];
}

/**
 * Update bundle request
 */
export interface UpdateBundleRequest {
    name?: string;
    description?: string;
    permissions?: PermissionsGrant[];
    status?: Status;
}

/**
 * List bundles response
 */
export interface ListBundlesResponse {
    bundles: PermissionsBundle[];
}

export interface ListPublicBundlesResponse {
    bundles: PermissionsBundlePublic[];
}

export interface SharedPermissionBundlesSearchRequest
    extends OpaqueCursorSearchRequest {}

export interface SharedPermissionBundlesSearchPageResponse {
    bundles: PermissionsBundlePublic[];
    pagination: OpaqueCursorPagination;
}

/**
 * Policy bundle search (org-scoped, cursor-paginated).
 */
export interface PolicyPermissionBundlesSearchRequest extends OpaqueCursorSearchRequest {
    include_descendants?: boolean;
    show_deleted?: boolean;
}

export interface PolicyPermissionBundlesSearchPageResponse {
    bundles: PolicyPermissionBundleListItem[];
    pagination: OpaqueCursorPagination;
}

export interface PermissionSourceRoleAssignmentsSearchRequest
    extends OpaqueCursorSearchRequest {}

export interface PermissionSourceRoleAssignmentsSearchPageResponse {
    role_assignments: PermissionSourceRoleAssignmentListItem[];
    pagination: OpaqueCursorPagination;
}

export interface PolicyPermissionBundleRoleAssignmentListItem
    extends PermissionSourceRoleAssignmentListItem {
    is_granted: boolean;
}

export interface PolicyPermissionBundleRoleAssignmentsSearchRequest
    extends OpaqueCursorSearchRequest {}

export interface PolicyPermissionBundleRoleAssignmentsSearchPageResponse {
    role_assignments: PolicyPermissionBundleRoleAssignmentListItem[];
    pagination: OpaqueCursorPagination;
}

/**
 * Create/update policy bundles.
 *
 * Note: We intentionally reuse the same shape as shared bundle CRUD; the difference is the
 * owning organization scope (path param) and invariants enforced in backend.
 */
export interface CreatePolicyPermissionBundleRequest extends CreateSharedBundleRequest {}
export interface UpdatePolicyPermissionBundleRequest extends UpdateBundleRequest {}

export interface RepairAuditedPermissionRequest {
    permissions_bundle_uuid: string;
    permission_index: number;
    permission: PermissionsGrant;
}

/**
 * Policy grants: assign one or more policy bundles to a role assignment (many-to-many).
 *
 * The list endpoint returns only currently live grants. Each returned bundle still
 * exposes its own lifecycle status so the UI can distinguish active and disabled
 * policies without inferring grant state from row existence.
 */
export interface ListPolicyPermissionGrantsResponse {
    bundles: PermissionsBundlePublic[];
}

export interface GrantPolicyPermissionBundleRequest {
    permissions_bundle_uuid: string;
}

export interface ReorderPolicyPermissionGrantsRequest {
    permissions_bundle_uuids: string[];
}

export const PermissionGrantAuditFindingCode = {
    INVALID_PERMISSION_PAYLOAD: 'INVALID_PERMISSION_PAYLOAD',
    INVALID_POLICY_GRANT: 'INVALID_POLICY_GRANT',
    SOURCE_TARGET_PAIR_UNSUPPORTED: 'SOURCE_TARGET_PAIR_UNSUPPORTED',
    SOURCE_RESOURCE_NOT_FOUND: 'SOURCE_RESOURCE_NOT_FOUND',
    SOURCE_OUTSIDE_OBSERVABLE_UNIVERSE: 'SOURCE_OUTSIDE_OBSERVABLE_UNIVERSE',
} as const;

export type PermissionGrantAuditFindingCode =
    (typeof PermissionGrantAuditFindingCode)[keyof typeof PermissionGrantAuditFindingCode];

export interface PermissionGrantAuditFinding {
    permissions_bundle_uuid: string | null;
    permissions_bundle_name: string | null;
    permissions_bundle_is_personal: boolean | null;
    permission_index: number | null;
    resource_index: number | null;
    code: PermissionGrantAuditFindingCode;
    message: string;
    source_designator: PermissionSourceDesignator | null;
    raw_value_json: string | null;
    editable_permission: PermissionsGrant | null;
}

export interface RoleAssignmentPermissionAudit {
    role_assignment_uuid: string;
    user_uuid: string;
    user_display_name: string;
    primary_email: string;
    organization_uuid: string | null;
    organization_name: string | null;
    findings: PermissionGrantAuditFinding[];
}

export interface RoleAssignmentPermissionAuditResponse {
    audit: RoleAssignmentPermissionAudit;
}

export interface OrganizationPermissionAuditResponse {
    organization_uuid: string;
    organization_name: string;
    audits: RoleAssignmentPermissionAudit[];
}

/**
 * Role eligibility for actions
 */
export interface RoleEligibility {
    role: string;
    eligible_actions: AuthorizableAction[];
}

export const RoleSchema = z.enum(Role);
export const InternalRoleSchema = z.enum(AllInternalOnly);
export const OrganizationalRoleSchema = z.enum(AllOrganizationalOnly);
const PermissionGrantAuditFindingCodeSchemaBase = z.enum([
    PermissionGrantAuditFindingCode.INVALID_PERMISSION_PAYLOAD,
    PermissionGrantAuditFindingCode.INVALID_POLICY_GRANT,
    PermissionGrantAuditFindingCode.SOURCE_TARGET_PAIR_UNSUPPORTED,
    PermissionGrantAuditFindingCode.SOURCE_RESOURCE_NOT_FOUND,
    PermissionGrantAuditFindingCode.SOURCE_OUTSIDE_OBSERVABLE_UNIVERSE,
]);
export const PermissionGrantAuditFindingCodeSchema: z.ZodType<PermissionGrantAuditFindingCode> =
    PermissionGrantAuditFindingCodeSchemaBase;
const PermissionGrantSchemaBase = z
    .object({
        effect: PermissionEffectSchema.default('allow'),
        resource_type: z.enum(ResourceType),
        resources: z.array(PermissionSourceDesignatorSchema).min(1),
        actions: z.array(z.enum(ALL_AUTHORIZABLE_ACTIONS)).min(1),
    })
    .superRefine((value, context) => {
        for (const action of value.actions) {
            if (!isAllowedActionForResourceType(value.resource_type, action)) {
                context.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Action ${action} is not allowed for resource_type ${value.resource_type}`,
                    path: ['actions'],
                });
            }
        }
    });

export const PermissionGrantSchema: typeof PermissionGrantSchemaBase =
    PermissionGrantSchemaBase;

export const PermissionGrantArraySchema: z.ZodType<PermissionsGrant[]> = z.array(
    PermissionGrantSchema
);
export const PermissionsBundleSchema = z.object({
    permissions_bundle_id: z.number(),
    name: z.string(),
    description: z.string().nullable(), // Handles 'string | null'
    permissions: z.array(PermissionGrantSchema), // Validates the array of grants
    is_personal: z.boolean(),
    status: z.enum(Status),
});

export const PermissionResourceDisplayMetadataSchema: z.ZodType<PermissionResourceDisplayMetadata> =
    z.object({
        designator_key: z.string().min(1),
        label: z.string().min(1),
    });

export const PermissionsBundlePublicSchema: z.ZodType<PermissionsBundlePublic> = z.object({
    permissions_bundle_uuid: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    permissions: z.array(PermissionGrantSchema),
    is_personal: z.boolean(),
    status: z.enum(Status),
    resource_display_metadata: z.array(PermissionResourceDisplayMetadataSchema).optional(),
    permissions_deserialization_error: z.string().optional(),
});

export const PolicyPermissionBundleListItemSchema: z.ZodType<PolicyPermissionBundleListItem> =
    z.object({
        permissions_bundle_uuid: z.string().uuid(),
        name: z.string(),
        description: z.string().nullable(),
        permissions: z.array(PermissionGrantSchema),
        is_personal: z.boolean(),
        status: z.enum(Status),
        resource_display_metadata: z.array(PermissionResourceDisplayMetadataSchema).optional(),
        permissions_deserialization_error: z.string().optional(),
        granted_role_assignment_count: z.number().int().nonnegative(),
    });
const PermissionSourceRoleAssignmentListItemSchemaBase = z.object({
    role_assignment_uuid: z.string().uuid(),
    user_uuid: z.string().uuid(),
    user_display_name: z.string().min(1),
    primary_email: z.string().email(),
    role: z.enum(Role),
    organization_uuid: z.string().uuid(),
    organization_name: z.string().min(1),
});
export const PermissionSourceRoleAssignmentListItemSchema: z.ZodType<PermissionSourceRoleAssignmentListItem> =
    PermissionSourceRoleAssignmentListItemSchemaBase;

const PolicyPermissionBundleRoleAssignmentListItemSchemaBase =
    PermissionSourceRoleAssignmentListItemSchemaBase.extend({
        is_granted: z.boolean(),
    });
export const PolicyPermissionBundleRoleAssignmentListItemSchema: z.ZodType<PolicyPermissionBundleRoleAssignmentListItem> =
    PolicyPermissionBundleRoleAssignmentListItemSchemaBase;

const OrganizationScopedPermissionSourceOptionsResponseSchemaBase = z.object({
    enabled_ipw_system_ids: z.array(IpwResourceDesignatorSchema.shape.ipw_system_id),
    enabled_ipg_system_ids: z.array(IpgResourceDesignatorSchema.shape.ipg_system_id),
});
export const OrganizationScopedPermissionSourceOptionsResponseSchema: z.ZodType<OrganizationScopedPermissionSourceOptionsResponse> =
    OrganizationScopedPermissionSourceOptionsResponseSchemaBase;
export const RoleAssignmentSchema: z.ZodType<RoleAssignment> = z.object({
    role_assignment_id: z.number(),
    role_assignment_uuid: z.string().uuid().optional(),
    user_id: z.number(),
    organization_id: z.number().nullable(),
    organization_uuid: z.string().uuid().optional(),
    organization_status: z.enum(Status).nullable().optional(),
    status: z.enum(Status),
    role: z.enum(Role),
    organization_name: z.string().nullable(),
    organization_is_iso: z.boolean(),
    organization_is_merchant: z.boolean(),
    organization_is_customer: z.boolean(),
});

export const RoleAssignmentDetailedSchema: z.ZodType<RoleAssignmentDetailed> = z.object({
    role_assignment_id: z.number(),
    role_assignment_uuid: z.string().uuid().optional(),
    user_id: z.number(),
    organization_id: z.number().nullable(),
    organization_uuid: z.string().uuid().optional(),
    organization_status: z.enum(Status).nullable().optional(),
    status: z.enum(Status),
    role: z.enum(Role),
    organization_name: z.string().nullable(),
    organization_is_iso: z.boolean(),
    organization_is_merchant: z.boolean(),
    organization_is_customer: z.boolean(),
    permissions_bundle: PermissionsBundleSchema,
    combined_config: CombinedConfigResponseSchema,
});

export const RoleAssignmentGrantedPermissionPolicySummarySchema: z.ZodType<RoleAssignmentGrantedPermissionPolicySummary> =
    z.object({
        permissions_bundle_uuid: z.string().uuid(),
        name: z.string().min(1),
        status: z.enum(Status),
    });

export const RoleAssignmentPermissionsViewSchema: z.ZodType<RoleAssignmentPermissionsView> =
    z.object({
        role_assignment_uuid: z.string().uuid(),
        organization_uuid: z.string().uuid().nullable(),
        status: z.enum(Status),
        role: z.enum(Role),
        organization_name: z.string().nullable(),
        organization_is_iso: z.boolean(),
        organization_is_merchant: z.boolean(),
        organization_is_customer: z.boolean(),
        permissions_bundle: PermissionsBundlePublicSchema,
        granted_permission_policies: z.array(
            RoleAssignmentGrantedPermissionPolicySummarySchema
        ),
        combined_config: CombinedConfigResponseSchema,
        can_impersonate: z.boolean().optional(),
    });

export const PermissionGrantAuditFindingSchema: z.ZodType<PermissionGrantAuditFinding> = z.object({
    permissions_bundle_uuid: z.string().uuid().nullable(),
    permissions_bundle_name: z.string().nullable(),
    permissions_bundle_is_personal: z.boolean().nullable(),
    permission_index: z.number().int().nullable(),
    resource_index: z.number().int().nullable(),
    code: PermissionGrantAuditFindingCodeSchema,
    message: z.string(),
    source_designator: PermissionSourceDesignatorSchema.nullable(),
    raw_value_json: z.string().nullable(),
    editable_permission: PermissionGrantSchema.nullable(),
});

export const RoleAssignmentPermissionAuditSchema: z.ZodType<RoleAssignmentPermissionAudit> =
    z.object({
        role_assignment_uuid: z.string().uuid(),
        user_uuid: z.string().uuid(),
        user_display_name: z.string(),
        primary_email: z.string().email(),
        organization_uuid: z.string().uuid().nullable(),
        organization_name: z.string().nullable(),
        findings: z.array(PermissionGrantAuditFindingSchema),
    });

export const RoleAssignmentPermissionAuditResponseSchema: z.ZodType<RoleAssignmentPermissionAuditResponse> =
    z.object({
        audit: RoleAssignmentPermissionAuditSchema,
    });

export const OrganizationPermissionAuditResponseSchema: z.ZodType<OrganizationPermissionAuditResponse> =
    z.object({
        organization_uuid: z.string().uuid(),
        organization_name: z.string(),
        audits: z.array(RoleAssignmentPermissionAuditSchema),
    });

export const AllRoleAssignmentsWithPermissionsSchema = z.array(RoleAssignmentDetailedSchema);
